import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { Card, Typography, Spin, Space } from "antd";
import { UserOutlined } from "@ant-design/icons";
import IdentitySearchSelect from "../common/IdentitySearchSelect";
import { types, errorAction } from "@authzsvc/api-lib";
import { showErrorNotification } from "../../util";
import { ContentTable, SearchBar } from "@authzsvc/common-react-components";
import {
  defaultGroupTableColumns,
  DEFAULT_GROUP_TABLE_QUERY_FIELDS,
} from "../../constants";
import { getVisibleGroupsForIdentity } from "../../actions/api";

const MembershipSearch = () => {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedIdentity, setSelectedIdentity] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  /**
   * Fetches all groups with pagination support from a single endpoint
   * Includes retry logic for transient network errors
   * @param {Function} actionCreator - The action creator function
   * @param {Object} queryFilter - The query filter object
   * @param {string} actionType - The action type constant for error checking
   * @param {number} maxRetries - Maximum number of retry attempts per page (default: 3)
   * @returns {Promise<Object>} - { error: boolean, data?: Array, response?: Object }
   */
  const fetchAllPagesFromEndpoint = useCallback(
    async (actionCreator, queryFilter, actionType, maxRetries = 3) => {
      let allGroups = [];
      let token = null;
      let hasMore = true;

      while (hasMore) {
        const currentFilter = token ? { ...queryFilter, token } : queryFilter;
        let response = null;
        let lastError = null;

        // Retry logic for each page
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
          response = await dispatch(actionCreator(currentFilter));

          if (
            response.type === errorAction(actionType) ||
            !response.payload?.data
          ) {
            lastError = response;
            // If this is not the last attempt, wait before retrying
            if (attempt < maxRetries) {
              await new Promise((resolve) =>
                setTimeout(resolve, 1000 * (attempt + 1))
              );
              continue;
            }
            // All retries failed
            return { error: true, response: lastError };
          }

          // Success - break out of retry loop
          break;
        }

        const groups = response.payload.data;
        allGroups = allGroups.concat(groups);

        // Check if there's a next page
        const pagination = response.payload.pagination;
        if (pagination && pagination.token) {
          token = pagination.token;
        } else {
          hasMore = false;
        }
      }

      return { error: false, data: allGroups };
    },
    [dispatch]
  );

  const columnsWithSorting = defaultGroupTableColumns.map((column) => ({
    ...column,
    sorter: (a, b) => {
      const aValue = a[column.dataIndex];
      const bValue = b[column.dataIndex];

      if (typeof aValue === "string" && typeof bValue === "string") {
        return aValue.localeCompare(bValue);
      }

      if (aValue && bValue && aValue.displayName && bValue.displayName) {
        return aValue.displayName.localeCompare(bValue.displayName);
      }

      if (aValue < bValue) return -1;
      if (aValue > bValue) return 1;
      return 0;
    },
  }));

  const fetchVisibleGroups = useCallback(
    async (text = "", filter = "Contains") => {
      if (!selectedIdentity) return;

      setLoading(true);

      // Build query filter with all parameters = true
      const queryFilter = {
        field: DEFAULT_GROUP_TABLE_QUERY_FIELDS,
        includeOpen: true,
        includeOwned: true,
        includeAdministratorOf: true,
        includeReaderOf: true,
        includeModeratorOf: true,
        includeMemberOf: true,
      };

      // Add search filter if text is provided
      if (text && text.trim().length > 0) {
        queryFilter.filter = `groupIdentifier:${filter}:${text.trim()}`;
      }

      // Create a wrapper function that includes the identity ID
      const fetchVisibleGroupsAction = (filter) =>
        getVisibleGroupsForIdentity(selectedIdentity.id, filter);

      const result = await fetchAllPagesFromEndpoint(
        fetchVisibleGroupsAction,
        queryFilter,
        types.GET_VISIBLE_GROUPS_FOR_IDENTITY
      );

      if (result.error) {
        showErrorNotification(
          "Failed to load visible groups",
          result.response?.payload?.data?.message ||
            "An error occurred while loading groups for this identity"
        );
        setGroups([]);
      } else {
        setGroups(result.data);
      }

      setLoading(false);
    },
    [selectedIdentity, fetchAllPagesFromEndpoint]
  );

  const handleSearch = useCallback(
    async (text, filter) => {
      await fetchVisibleGroups(text, filter);
    },
    [fetchVisibleGroups]
  );

  const handleIdentitySelect = async (identity) => {
    setSelectedIdentity(identity);
    // Will trigger fetchVisibleGroups via useEffect
  };

  const clearIdentityFilter = () => {
    setSelectedIdentity(null);
    setGroups([]);
    setSearchValue("");
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [groups]);

  useEffect(() => {
    if (selectedIdentity) {
      fetchVisibleGroups(searchValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIdentity]);

  return (
    <Card
      title={
        <Space size={0}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            <UserOutlined style={{ marginRight: 8, color: "#bfbfbf" }} />
            Membership search
          </Typography.Title>
        </Space>
      }
    >
      <p>
        Search for a user to view their direct group memberships. For privacy
        reasons, this search only returns groups that are visible to you and in
        which the user is a direct member. Group visibility may be granted
        either by the group's visibility settings or by your administrative
        rights over the group.
      </p>

      <p>
        To subscribe or remove members in bulk please go to{" "}
        <Link to="/groups/admin">Groups I own or manage</Link>. To view your own
        membership in groups please go to{" "}
        <Link to="/group/memberships/my">Groups I'm in</Link>.
      </p>

      <Spin spinning={loading}>
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <div style={{ minWidth: 280, flex: "0 0 240px" }}>
            <IdentitySearchSelect
              placeholder="Select member to view their groups"
              onSelect={handleIdentitySelect}
              onClear={clearIdentityFilter}
              value={selectedIdentity}
              style={{ width: "100%" }}
            />
          </div>
          <div style={{ flex: 1, minWidth: 280 }}>
            <SearchBar
              onSearch={handleSearch}
              searchValue={searchValue}
              onSearchValueChange={setSearchValue}
              filter={true}
              filterPlacement="before"
              allowClear={true}
              placeholder="Search groups..."
              disabled={!selectedIdentity}
              disabledTooltip="Please select a user first to search their groups"
            />
          </div>
        </div>

        <ContentTable
          rowKey="id"
          pagination={{
            current: currentPage,
            onChange: (page) => setCurrentPage(page),
            onShowSizeChange: (current, size) => setPageSize(size),
            showSizeChanger: true,
            hideOnSinglePage: true,
            showTotal: (total) =>
              `Total of ${total} ${total === 1 ? "group" : "groups"}`,
            defaultPageSize: 10,
          }}
          columns={columnsWithSorting}
          dataSource={groups}
        />
        {groups.length <= pageSize && groups.length > 0 && (
          <div style={{ marginTop: 16, textAlign: "right" }}>
            <div
              style={{
                display: "inline-block",
                height: "32px",
                lineHeight: "30px",
                color: "rgba(0, 0, 0, 0.88)",
                fontSize: "14px",
              }}
            >
              Total of {groups.length}{" "}
              {groups.length === 1 ? "group" : "groups"}
            </div>
          </div>
        )}
      </Spin>
    </Card>
  );
};

export default MembershipSearch;
